import { AbstractControl, FormControl, ValidationErrors } from '@angular/forms';
import { FamilyDocumentType } from '@models/family';
import { isValid as isValidIBAN } from 'iban';
import { Moment } from 'moment';
import {
  isFutureDate,
  isOlderThan, isValidAnswer,
  isValidEmail, isValidFrameNumber, isValidMobile,
  isValidPartialNie,
  isValidPartialNif,
  isValidPartialNifOrCif,
  isValidPassword, isValidPhone, isValidPlateCar,
  isValidPlateMotorBike, isValidPostalCode, validateCif, validateDocument, validateNifNiePassport
} from './utils';


export function ValidatorHasUpper(control: FormControl): ValidationErrors | null {
  const hasUpper = /[A-Z]/.test(control.value);
  if (!hasUpper) {
      return { hasUpper: true };
  }
  return null;
}

export function ValidatorHasLower(control: FormControl): ValidationErrors | null {
  const hasLower = /[a-z]/.test(control.value);
  if (!hasLower) {
      return { hasLower: true };
  }
  return null;
}

export function ValidatorHasNumber(control: FormControl): ValidationErrors | null {
  const hasNumber = /\d/.test(control.value);
  if (!hasNumber) {
      return { hasNumber: true };
  }
  return null;
}

export function ValidatorHasSpecialCharacter(control: FormControl): ValidationErrors | null {
  const hasSpecialCharacter = /[`!@#$%^&*()_\[\]{};':"\\|,.<>\/?~]/.test(control.value);
  if (!hasSpecialCharacter) {
      return { hasSpecialCharacter: true };
  }
  return null;
}

export function ValidatorNonEmptyPartialDocNumber(
  control: AbstractControl
): ValidationErrors | null {
  const nifValid = (control.value) ? isValidPartialNifOrCif(control.value) : null;
  const nieValid = (control.value) ? isValidPartialNie(control.value) : null;
  if (control.value && !nifValid && !nieValid) {
    return { validPartialDocNumber: true };
  }
  return null;
}

export function ValidatorDocNumber(
  control: AbstractControl
): ValidationErrors | null {
  const documentValid = (control.value) ? validateNifNiePassport(control.value) : null;
  if (control.value && !documentValid) {
    return { validPartialDocNumber: true };
  }
  return null;
}

export function ValidatorDocNumberFamilies(
  control: AbstractControl
): ValidationErrors | null {
  const documentValid = (control.value) ? validateDocument(control.value) : null;
  if (control.value && !documentValid) {
    return { validPartialDocNumber: true };
  }
  return null;
}

export function ValidatorIsCif(
  control: AbstractControl
): ValidationErrors | null {
  const documentValid = (control.value) ? validateCif(control.value) : null;
  if (control.value && !documentValid) {
    return { validCif: true };
  }
  return null;
}

export function ValidatorNonEmptyPartialNie(
  control: AbstractControl
): ValidationErrors | null {
  const nieValid = (control.value) ? isValidPartialNie(control.value) : null;
  if (control.value && !nieValid) {
    return { validPartialDocNumber: true };
  }
  return null;
}

export function ValidatorNonEmptyPartialNif(
  control: AbstractControl
): ValidationErrors | null {
  const nieValid = (control.value) ? isValidPartialNif(control.value) : null;
  if (control.value && !nieValid) {
    return { validPartialDocNumber: true };
  }
  return null;
}

export function ValidatorIsNifOrNie(control: AbstractControl): ValidationErrors | null {
  if (control.value !== FamilyDocumentType.NIE && control.value !== FamilyDocumentType.NIF) {
    return { isNotNifNorNie: true };
  }
  return null;
}

export function ValidatorIBAN(control: AbstractControl): ValidationErrors | null {
  if (!isValidIBAN(control.value)) {
    return { validIBAN: true };
  }
  return null;
}
export function ValidatorEmail(
  control: AbstractControl
): ValidationErrors | null {
  if (!isValidEmail(control.value)) {
    return { validEmail: true };
  }
  return null;
}
export function ValidatorNonEmptyEmail(control: AbstractControl): ValidationErrors | null {
  if (control.value && !isValidEmail(control.value)) {
    return { validEmail: true };
  }
  return null;
}
export function ValidatorEqualEmail(control: AbstractControl): ValidationErrors | null {
  if (control.value && control['_parent'] && control.value !== control['_parent'].value.email) {
    return { equalEmail: true };
  }
  return null;
}

export function ValidatorMobile(
  control: AbstractControl
): ValidationErrors | null {
  if (!isValidMobile(control.value)) {
    return { validPhone: true };
  }
  return null;
}
export function ValidatorNonEmptyMobile(control: AbstractControl): ValidationErrors | null {
  if (control.value && !isValidMobile(control.value)) {
    return { validPhone: true };
  }
  return null;
}

export function ValidatorHousePhone(
  control: AbstractControl
): ValidationErrors | null {
  if (!isValidPhone(control.value)) {
    return { validHousePhone: true };
  }
  return null;
}

export function ValidatorMobileOrHousePhone(
  control: AbstractControl
): ValidationErrors | null {
  const mobileValid = isValidMobile(control.value);
  const housePhoneValid = isValidPhone(control.value);
  if (mobileValid || housePhoneValid) {
    return null;
  }
  return { validMobileOrHousePhone: true };
}

export function ValidatorNonEmptyMobileOrHousePhone(
  control: AbstractControl
): ValidationErrors | null {
  const mobileValid = isValidMobile(control.value);
  const housePhoneValid = isValidPhone(control.value);
  if (control.value && !mobileValid && !housePhoneValid) {
    return { validMobileOrHousePhone: true };
  }
  return null;
}

export function ValidatorPostalCode(
  control: AbstractControl
): ValidationErrors | null {
  if (!isValidPostalCode(control.value)) {
    return { validPostalCode: true };
  }
  return null;
}

export function ValidatorNonEmptyPostalCode(
  control: AbstractControl
): ValidationErrors | null {
  if (control.value && !isValidPostalCode(control.value)) {
    return { validPostalCode: true };
  }
  return null;
}

export function ValidatorAdult(
  control: AbstractControl
): ValidationErrors | null {
  const error = { validAdult: true };
  const { value }: { value: string | Moment } = control;
  if (value) {
    try {
      if (!isOlderThan(value, 18, 'YYYY-MM-DD')) {
        return error;
      }
    } catch (_) {
      return error;
    }
  }
  return null;
}

export function ValidatorOlderThan16(control: AbstractControl) {
  const error = { validOlderThan16: true };
  const { value }: { value: string | Moment } = control;
  if (value) {
    try {
      if (!isOlderThan(value, 16, 'YYYY-MM-DD')) {
        return error;
      }
    } catch (_) {
      return error;
    }
  }
  return null;
}

export function ValidatorFutureDate(control: AbstractControl) {
  const error = { validFutureDate: true };
  const { value }: { value: string | Moment } = control;
  if (value) {
    try {
      if (isFutureDate(value, 'YYYY-MM-DD')) {
        return error;
      }
    } catch (_) {
      return error;
    }
  }
  return null;
}

export function ValidatorYoungerThan120Years(control: AbstractControl) {
  const error = { validYoungerThan120Years: true };
  const { value }: { value: string | Moment } = control;
  if (value) {
    try {
      if (isOlderThan(value, 120, 'YYYY-MM-DD')) {
        return error;
      }
    } catch (_) {
      return error;
    }
  }
  return null;
}

export function ValidatorDateIsAfter(date) {
  return function dateIsAfter(
    control: AbstractControl
  ): ValidationErrors | null {
    const error = { validAdult: true };
    if (control.value) {
      try {
        if (!control.value.isAfter(date)) {
          return error;
        }
      } catch (_) {
        return error;
      }
    }
    return null;
  };
}

export function ValidatorTrim(
  control: AbstractControl
): ValidationErrors | null {
  if (!control.value || !control.value.trim()) {
    return { trim: true };
  }
  return null;
}

export function ValidatorNonSpecialCharacters(
  control: AbstractControl
): ValidationErrors | null {
  if (!isValidPassword(control.value)) {
    return { validNonSpecialCharacter: true };
  }
}

export function ValidatorNonSpecialCharactersSecurityAnswer(
  control: AbstractControl
): ValidationErrors | null {
  if (!isValidAnswer(control.value)) {
    return { validNonSpecialCharacter: true };
  }
}

export function ValidatorMatchValues(field: string) {
  let field2: FormControl;
  return function checkValue(form: FormControl) {
    if (form.value !== '') {
      field2 = form.parent.get(field) as FormControl;
      if (form.value !== '' && form.value !== field2.value) {
        return { validMatch: true };
      }
    }
  };
}

export function ValidatorMatchValuesUppercase(field: string) {
  let field2: FormControl;
  return function checkValue(form: FormControl) {
    if (form.value !== '') {
      field2 = form.parent.get(field) as FormControl;
      if (form.value.toUpperCase() !== '' && form.value.toUpperCase() !== field2.value.toUpperCase()) {
        return { validMatch: true };
      }
    }
  };
}

export function ValidatorDifferentValues(field: string) {
  let field2: FormControl;
  return function checkValue(form: FormControl) {
    if (form.value !== '') {
      field2 = form.parent.get(field) as FormControl;
      if (form.value === field2.value) {
        return { validMatch: true };
      }
    }
  };
}

export function ValidatorPartialNifOrNie(
  control: AbstractControl
): ValidationErrors | null {
  const nifValid = isValidPartialNif(control.value);
  const nieValid = isValidPartialNie(control.value);
  if (control.value && !nifValid && !nieValid) {
    return { validPartialNifOrNie: true };
  }
  return null;
}

export function ValidatorAntiquity(
  control: AbstractControl
): ValidationErrors | null {
  return control.value <= 10 ? null : { validAntiquity: true };
}

export function GroupValidatorDateBefore(dateProp: string, toDateProp: string) {
  return function groupValidator(
    group: AbstractControl
  ): ValidationErrors | null {
    const fromCtrl = group.get(dateProp);
    const toCtrl = group.get(toDateProp);

    if (!toCtrl.value || !fromCtrl.value) {
      return null;
    }

    return new Date(fromCtrl.value) > new Date(toCtrl.value)
      ? { [dateProp + 'OutOfRangeMax']: true }
      : null;
  };
}

export function ValidatorPlateCar(
  control: AbstractControl
): ValidationErrors | null {
  if (!isValidPlateCar(control.value)) {
    return { validPlateCar: true };
  }
  return null;
}

export function ValidatorPlateMotorBike(
  control: AbstractControl
): ValidationErrors | null {
  if (!isValidPlateMotorBike(control.value)) {
    return { validPlateMotorbike: true };
  }
  return null;
}

export function ValidatorFrameNumber(
  control: AbstractControl
): ValidationErrors | null {
  if (!isValidFrameNumber(control.value)) {
    return { validFrameNumber: true };
  }
  return null;
}

export function ValidatorRequirements(control: FormControl): ValidationErrors | null {
  if (control.value.length < 8 || ValidatorHasLower(control) || ValidatorHasUpper(control) ||
    ValidatorHasNumber(control) || ValidatorHasSpecialCharacter(control)) {
      return {  allRequirements: true };
  }
  return null;
}

export function ValidatorAddress(control: FormControl): ValidationErrors | null {
   const regex = new RegExp(/^([0-9a-zA-ZÁÉÍÓÚáéíóú\s\u00f1\u00d1.ÚçàãâèêòùÇÀÃÂÈÊÒÙ@_\\-])*$/g)
  if (!regex.test(control.value)) {
    return { validAddress: true };
  }
  return null;
}

export function ValidatorNonWithOutNumber(control: FormControl): ValidationErrors | null {
  const regex = new RegExp(/^(?:(?!SN|S N|S\/N)[0-9a-zA-ZÁÉÍÓÚáéíóú\s\u00f1\u00d1.ÚçàãâèêòùÇÀÃÂÈÊÒÙ@_\\-])*$/g)
  if (!regex.test(control.value)) {
    return { validAddress: true };
  }
  return null;
}
